/**
 * 一覧ビュー（docs/01-requirements.md §5.17）。起動画面になり得るビューの 1 つ。
 *
 * タイムラインの認知負荷が高い件（`06-open-questions.md` #13）の改善までの**つなぎ**として
 * 併存させる素朴なリスト。ADR-030 の主目的は「後で畳めること」なので、次を守る:
 *
 * - **個別閲覧へ遷移することだけ**を行う。削除・カテゴリ変更を足さない（§8 スコープ外）。
 *   一覧にしかない動線が育つと畳めなくなる。
 * - 見た目は**カード主体**（ADR-035）。**畳みやすさを保つため、このファイルと
 *   `.note-list*` の CSS だけで完結させてある**（タイムライン側の `.note-card*` とは
 *   1 行も共有しない）。ADR-030 が言う「素朴」は情報の少なさであって、見た目ではない。
 * - タイムライン側がこのファイルの何かを import しない（依存は一方向）。
 * - 本文は `MarkdownView` を通さずプレーンテキストで出す。画像やコードブロックで
 *   行の高さが暴れると「素朴な一覧」でなくなるため。
 */

import { Link } from 'react-router-dom';
import { AppHeader } from '@app/components/AppHeader';
import { ErrorNotice } from '@app/components/ErrorNotice';
import { Icon } from '@app/components/icons';
import { NoteFilterBar } from '@app/components/NoteFilterBar';
import { subjectLabel } from '@app/components/subject-label';
import { useCategories } from '@app/components/useCategories';
import { ViewSwitcher } from '@app/components/ViewSwitcher';
import { useNoteFeed } from '@app/hooks/useNoteFeed';
import { useStartupMaintenance } from '@app/save/useStartupMaintenance';
import { loadViewState, saveViewState, type ViewState } from '@app/views/view-preference';
import { deriveSummaryText, displayTitle } from '@shared/markdown-input';
import type { NoteSummary } from '@shared/types';
import { useCallback, useState } from 'react';

/** 1 行が軽いのでタイムラインより多めに読む。 */
const PAGE_SIZE = 30;

export function NoteListPage() {
  const categories = useCategories();

  const [view, setView] = useState<ViewState>(loadViewState);
  const { notes, loading, done, error, reload, sentinelRef } = useNoteFeed(view, PAGE_SIZE);

  // 起動画面になり得るので、ここでも未送信ドラフトを回収する（§5.1 P0 / 絶対規則2）。
  useStartupMaintenance();

  const updateView = useCallback((patch: Partial<ViewState>) => {
    setView((prev) => {
      const next = { ...prev, ...patch };
      saveViewState(next);
      return next;
    });
  }, []);

  return (
    <div className="page note-list-page">
      <AppHeader
        title={<ViewSwitcher current="list" />}
        right={
          <nav className="app-header__nav" aria-label="移動">
            <Link className="icon-button" to="/search" aria-label="検索" title="検索">
              <Icon name="search" />
            </Link>
            <Link className="icon-button" to="/trash" aria-label="ゴミ箱" title="ゴミ箱">
              <Icon name="trash" />
            </Link>
            <Link className="icon-button" to="/settings" aria-label="設定" title="設定">
              <Icon name="settings" />
            </Link>
          </nav>
        }
      />

      <NoteFilterBar view={view} onChange={updateView} />

      <main className="page__body">
        {error != null && <ErrorNotice error={error} onRetry={reload} />}

        {notes.length === 0 && !loading && error == null && (
          <div className="empty-state">
            <p>まだノートがありません。</p>
            <p className="muted">思いついたことを、そのまま書き始められます。</p>
            <Link className="al-button al-button--primary" to="/notes/new">
              最初のノートを書く
            </Link>
          </div>
        )}

        <ul className="note-list">
          {notes.map((note) => (
            <NoteListItem
              key={note.id}
              summary={note}
              categoryName={categories.nameOf(note.category_id)}
            />
          ))}
        </ul>

        {loading && <p className="muted centered-note">読み込み中…</p>}
        {!done && <div ref={sentinelRef} className="timeline__sentinel" aria-hidden="true" />}
        {done && notes.length > 0 && <p className="muted centered-note">ここまでです</p>}
      </main>

      <Link
        className="fab"
        to="/notes/new"
        aria-label="新しいノートを書く"
        title="新しいノートを書く"
      >
        <Icon name="plus" size={26} />
      </Link>
    </div>
  );
}

interface NoteListItemProps {
  summary: NoteSummary;
  categoryName: string | null;
}

function NoteListItem({ summary, categoryName }: NoteListItemProps) {
  // タイトル未入力なら「無題」。本文で代替しない（ADR-031）。内容の手がかりはサマリーが担う。
  const title = displayTitle(summary.title);
  // 全文は取りに行かない（1 行あたり 1 リクエストになる）。抜粋で足りる範囲だけ出す。
  const text = deriveSummaryText(summary.excerpt);

  return (
    <li className="note-list__item">
      {/* **カード全体がリンク**（ADR-035 / §5.17 P0）。
          面として塗る以上、塗られた範囲と押せる範囲が一致していなければならない。
          食い違うと**選択状態に見える**（T-009 で実際に指摘を受けた）。
          誤タップの代償は「個別閲覧が開くだけ」で、戻れば済む。 */}
      <Link className="note-list__card" to={`/notes/${summary.id}`}>
        <div className="note-list__meta">
          <span className="note-list__date">
            {subjectLabel(summary.subject_date, summary.subject_date_end)}
          </span>
          {categoryName && (
            <span className="al-chip al-chip--lilac mm-category-badge note-list__category">
              {categoryName}
            </span>
          )}
        </div>

        <span className="note-list__title-text">{title}</span>

        {text !== '' && <p className="note-list__summary">{text}</p>}
      </Link>
    </li>
  );
}
